import { experimentSampleSizeCalculator } from '@gonfalon/dogfood-flags/dogfood-flags';
import { sampleSizeToDuration } from '@gonfalon/experiments/sampleSizeToDuration';
import type {
  CalculatorInputs,
  CalculatorResult,
  Experiment,
  ExperimentAnalysis,
  Iteration,
  SampleSizeTarget,
  SampleSizeTargetInput,
  SampleSizeTargetInputs,
} from '@gonfalon/experiments/types';
import { getMetricKind, MetricKind } from '@gonfalon/metrics/MetricKind';
import type { Metric } from '@gonfalon/metrics/types';
import type { zQuantile as ZQuantileImport } from '@launchdarkly/power-analysis';
import { queryOptions } from '@tanstack/react-query';
import { isEqual } from 'lodash';

// Reuse the shared @launchdarkly/power-analysis package for the numerically-tricky inverse-normal
// quantile (zQuantile), rather than forking that approximation. We deliberately do NOT use the
// package's powerAnalysis(): v1.0.12 throws when `alpha >= 1 - power`, but the calculator's
// significance/power options include valid combinations at that boundary (e.g. 95%/95%, 90%/90%),
// so the closed-form total is computed locally with the package's zQuantile.
export type ZQuantileFn = typeof ZQuantileImport;

// Loaded lazily so the package stays in its own chunk instead of the experiments-dashboard bundle.
export async function loadZQuantile(): Promise<ZQuantileFn> {
  const { zQuantile } = await import(/* webpackChunkName: "power-analysis" */ '@launchdarkly/power-analysis');
  return zQuantile;
}

// The math lives in its own chunk, imported on demand and shared across every mount that needs it.
export const powerAnalysisEngineQuery = queryOptions({
  queryKey: ['power-analysis-engine'],
  queryFn: loadZQuantile,
  staleTime: Infinity,
});

// The one place the flag is checked, so a target persisted before the flag was turned off stays hidden.
export function attachedSampleSizeTarget(target?: SampleSizeTarget | null): SampleSizeTarget | null {
  if (!experimentSampleSizeCalculator()) {
    return null;
  }

  return target ?? null;
}

// A target defines a stopping point only for a fixed horizon, so sequential testing never reaches one.
export function reachedAttachedSampleSizeTarget({
  experiment,
  iteration,
  analysis,
}: {
  experiment: Pick<Experiment, 'analysisConfig'>;
  iteration: Pick<Iteration, 'sampleSizeTarget'>;
  analysis?: Pick<ExperimentAnalysis, 'treatments'>;
}): boolean {
  if (experiment.analysisConfig?.sequentialTestingEnabled) {
    return false;
  }

  const target = attachedSampleSizeTarget(iteration.sampleSizeTarget);
  if (!target || !analysis) {
    return false;
  }

  const observedExposures = analysis.treatments.reduce<number>((acc, { trafficCount }) => acc + (trafficCount ?? 0), 0);

  return observedExposures >= target.totalSampleSize;
}

// Standard A/B: the calculator's math, copy, and saved targets are all fixed to control + treatment.
export const CALCULATOR_VARIATIONS = 2;

// Presets for the builder's own significance control, which the calculator reuses so a custom
// threshold set there survives the round trip. Any value in (0, 1) is accepted, not just these.
export const SIGNIFICANCE_LEVEL_OPTIONS = [0.1, 0.05, 0.01];

export const POWER_OPTIONS = [
  { id: '0.8', label: '80%' },
  { id: '0.9', label: '90%' },
  { id: '0.95', label: '95%' },
];

export const DEFAULT_INPUTS: CalculatorInputs = {
  metricType: 'conversion',
  baselineRate: 0.1,
  baselineMean: 100,
  baselineStdDev: 50,
  minimumDetectableEffect: 0.05,
  trafficAllocation: 0.5,
  dailyTraffic: 10000,
  significanceLevel: 0.05,
  power: 0.8,
  testDirection: 'two-sided',
};

// Two-sample normal-approximation sample size, using the package's zQuantile (injected) for the
// inverse-normal quantile so the numbers match LaunchDarkly's public calculator. Standard A/B
// (balanced 50/50 split, equal variance), so the imbalance/ancova design-effect terms are 1 and
// drop out — leaving total = (2k / (effect·q))². Returns null when the inputs are out of domain.
export function computeResult(inputs: CalculatorInputs, zQuantile: ZQuantileFn): CalculatorResult | null {
  // Derive the control mean and its standard deviation from the metric type.
  let controlMean: number;
  let controlSd: number;
  if (inputs.metricType === 'conversion') {
    const p = inputs.baselineRate;
    if (p === undefined || !(p > 0 && p < 1)) {
      return null;
    }
    controlMean = p;
    controlSd = Math.sqrt(p * (1 - p));
  } else {
    const { baselineMean, baselineStdDev } = inputs;
    if (baselineMean === undefined || baselineStdDev === undefined) {
      return null;
    }
    if (!(baselineMean > 0) || !(baselineStdDev > 0)) {
      return null;
    }
    controlMean = baselineMean;
    controlSd = baselineStdDev;
  }

  const effect = inputs.minimumDetectableEffect;
  const dailyAllocated = inputs.dailyTraffic * inputs.trafficAllocation;

  // Standardized effect denominator: relative MDE against the control mean / its SD.
  const q = controlMean / controlSd;
  const alpha = inputs.significanceLevel;
  const power = inputs.power;
  const tails = inputs.testDirection === 'two-sided' ? 2 : 1;

  if (!(effect > 0) || !(dailyAllocated > 0) || !(q !== 0 && Number.isFinite(q))) {
    return null;
  }
  if (!(alpha > 0 && alpha < 1) || !(power > 0 && power < 1)) {
    return null;
  }

  // k = z(power) + z(1 - alpha/tails); total N across both arms = (2k / (effect·q))².
  const k = Math.abs(zQuantile(power) + zQuantile(1 - alpha / tails));
  const totalTwoArm = ((2 * k) / (effect * q)) ** 2;

  if (!Number.isFinite(totalTwoArm) || totalTwoArm <= 0) {
    return null;
  }

  // Variations are locked to 2 (standard A/B); per-arm is half the total across the two arms.
  const samplePerVariation = Math.ceil(totalTwoArm / CALCULATOR_VARIATIONS);
  const totalSample = samplePerVariation * CALCULATOR_VARIATIONS;
  const duration = sampleSizeToDuration(totalSample, dailyAllocated);
  const durationDays = duration === null ? 0 : Math.ceil(duration);

  return { samplePerVariation, totalSample, durationDays };
}

// Kinds whose values are continuous, so the calculator sizes them from a mean and a standard
// deviation rather than a conversion rate. `kind` alone cannot tell these apart — a 'custom' metric
// is numeric or conversion depending on its aggregation — so this reuses the shared derivation.
const NUMERIC_METRIC_KINDS: string[] = [MetricKind.NUMERIC, MetricKind.COUNT, MetricKind.COUNT_DISTINCT];

/** What the calculator can size, plus the metrics it cannot size at all. */
export type MetricSizing = CalculatorInputs['metricType'] | 'unsupported';

// Which baseline fields a fresh calculation opens on. A metric we cannot place, or cannot size,
// falls back to the default rather than guessing — the user is not offered one for `unsupported`.
export function toMetricType(primaryMetric: Metric | undefined): CalculatorInputs['metricType'] {
  return toMetricTypeIfKnown(primaryMetric) === 'numeric' ? 'numeric' : DEFAULT_INPUTS.metricType;
}

// Summing occurrences or counting distinct units both produce a quantity per unit, which the
// calculator sizes from a mean; averaging produces a rate.
const NUMERIC_AGGREGATIONS: string[] = ['sum', 'count_distinct'];

// Kinds that measure either a rate or a quantity depending on how they aggregate, so the
// aggregation is the only thing that separates them. `getMetricKind` reports CLICK and PAGEVIEW
// whatever the aggregation, because it names the metric for display rather than for analysis.
const AMBIGUOUS_METRIC_KINDS: string[] = [MetricKind.CUSTOM, MetricKind.TRACE, MetricKind.CLICK, MetricKind.PAGEVIEW];

type MetricTypeSource = Pick<Metric, 'unitAggregationType' | 'isNumeric'> & {
  kind?: string;
  analysisType?: 'mean' | 'percentile';
};

/**
 * How the calculator should size a metric, or `undefined` when what we hold cannot say — a saved
 * draft's metric list omits the aggregation, so callers resolve the metric before deciding.
 * `unsupported` is a settled answer, not a gap: the metric cannot be sized here at all.
 */
export function toMetricTypeIfKnown(metric: MetricTypeSource | undefined): MetricSizing | undefined {
  const { kind, unitAggregationType, isNumeric, analysisType } = metric ?? {};

  if (!kind) {
    return undefined;
  }
  // The maths sizes a difference in means, which says nothing about a percentile.
  if (analysisType === 'percentile') {
    return 'unsupported';
  }
  if (isNumeric === true) {
    return 'numeric';
  }
  if (unitAggregationType !== undefined) {
    return NUMERIC_AGGREGATIONS.includes(unitAggregationType) ? 'numeric' : 'conversion';
  }

  return AMBIGUOUS_METRIC_KINDS.includes(kind) ? undefined : toMetricTypeFromKind(kind);
}

function toMetricTypeFromKind(kind: string): MetricSizing {
  return NUMERIC_METRIC_KINDS.includes(getMetricKind(kind)) ? 'numeric' : 'conversion';
}

/** Whether switching to `nextMetric` leaves the calculation sizing a metric of the other type. */
export function metricChangeDropsTarget(
  target: SampleSizeTarget | null | undefined,
  nextMetric: MetricTypeSource | undefined,
): boolean {
  return Boolean(target) && toMetricTypeIfKnown(nextMetric) !== target?.inputs.metricType;
}

/** The settings the experiment owns and the calculator computes with, in the experiment's shapes. */
export type ExperimentDesign = {
  significanceThreshold?: string;
  testDirection?: string;
  /** The share of the audience in the experiment, as a percentage. */
  experimentAllocation?: number;
};

// A stored target holds only what the calculation owns; the rest comes off the experiment, so the
// two can never disagree. A design that states nothing — the standalone calculator has no
// experiment behind it — falls back to the same defaults a fresh calculation opens on.
export function toCalculatorInputs(inputs: SampleSizeTargetInputs, design: ExperimentDesign): CalculatorInputs {
  return {
    ...inputs,
    significanceLevel: toSignificanceLevel(design.significanceThreshold) ?? DEFAULT_INPUTS.significanceLevel,
    testDirection: toTestDirection(design.testDirection) ?? DEFAULT_INPUTS.testDirection,
    trafficAllocation: toTrafficAllocation(design.experimentAllocation) ?? DEFAULT_INPUTS.trafficAllocation,
  };
}

/**
 * An attached target's outputs, recomputed against the design the experiment currently has. The
 * inputs cannot drift, but the sample size and duration were computed from the design as it stood
 * when the target was saved, so they can.
 *
 * Returns the target itself when nothing moved — so callers can compare by identity — and equally
 * when the engine has not loaded or the inputs fall outside the calculator's domain, neither of
 * which leaves anything more honest to show than the numbers that were saved.
 */
export function resizeTargetForDesign(
  target: SampleSizeTargetInput | undefined,
  design: ExperimentDesign,
  zQuantile: ZQuantileFn | undefined,
): SampleSizeTargetInput | undefined {
  if (!target || !zQuantile) {
    return target;
  }

  const result = computeResult(toCalculatorInputs(target.inputs, design), zQuantile);
  if (!result) {
    return target;
  }

  const resized = {
    ...target,
    totalSampleSize: result.totalSample,
    estimatedDurationDays: result.durationDays,
  };

  return isEqual(resized, target) ? target : resized;
}

// The experiment states significance as a percentage ('5'); the calculator holds the same figure as
// a proportion. The builder's control accepts any value in the range, not just the presets.
export function toSignificanceLevel(significanceThreshold: string | undefined): number | undefined {
  const threshold = Number(significanceThreshold);
  if (!significanceThreshold || !Number.isFinite(threshold) || threshold <= 0 || threshold >= 100) {
    return undefined;
  }

  return Math.round((threshold / 100) * 1e6) / 1e6;
}

export function toTestDirection(testDirection: string | undefined): CalculatorInputs['testDirection'] | undefined {
  return testDirection === 'one-sided' || testDirection === 'two-sided' ? testDirection : undefined;
}

// The builder states the audience as a percentage (100), the calculator as a proportion (1).
export function toTrafficAllocation(experimentAllocation: number | undefined): number | undefined {
  if (experimentAllocation === undefined || !Number.isFinite(experimentAllocation) || experimentAllocation <= 0) {
    return undefined;
  }

  return Math.min(1, Math.round((experimentAllocation / 100) * 1e6) / 1e6);
}
